<template>
  <div id="app">
    <router-view ref="view" :class="{ 'opacity-0': hideView }" @user-login-done="onUserLoginDone" />
    <garment
      v-show="showGarment"
      :src="garmentSource"
      @login="startUserLogin"
      @cancel="closeGarment"
    />
    <qr-scan
      v-show="isScanningQr"
      my-code
      :show-camera="isScanningQr"
      :message="$t('welcome.scan_code_hint')"
      :invalid="qrCodeInvalid"
      @cancel="cancelQrScan"
      @dismiss-invalid="qrCodeInvalid = false"
    />
    <app-menu v-show="showMenu" @mtm-login-done="onMtmUserLoginDone" />
    <console />
  </div>
</template>
<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import throttle from 'lodash/throttle';
import CacheManager from '@/modules/cache-manager';
import AudioManager from '@/modules/audio-manager';
import Storage from '@/modules/storage';
import ApiActions from '@/modules/api-actions';
import ScanProcess from '@/modules/scan-process';
import WebBT from '@/modules/web-bluetooth';
import { getCurrentBreakpoint } from '@/modules/utils';
import { EVENTS } from '@/events/hardware';
import { EVENTS as UI_EVENTS } from '@/events/ui';

import handleQrScan from '@/mixins/handle-qr-scan';

import Garment from '@/components/common/Garment.vue';
import QrScan from '@/components/Welcome/QrScan.vue';
import ErrorModal from '@/components/common/ErrorModal.vue';

import AppMenu from '@/components/DefaultCloudzet/common/Menu.vue';
import Console from '@/components/common/Console.vue';

const throttleConnect = throttle(
  callback => {
    callback();
  },
  3000,
  { leading: false }
);

export default {
  name: 'App',
  components: {
    Garment,
    QrScan,
    Console,
    AppMenu,
  },
  mixins: [handleQrScan],
  created() {
    this.$events.once(EVENTS.AUTH_LOADED, this.loginWithPreExistingAuth);
    this.$events.once(EVENTS.APP_READY, () => {
      console.log('app ready!');
      this.setAppReady(true);
      WebBT.disable();
      this.$events.emit(EVENTS.WEB_READY);
    });
    this.$events.on(EVENTS.BT_ERROR, this.handleBTerror);
    this.$events.on(EVENTS.BT_DATA_ERROR, this.handleDataError);
    this.$events.on(EVENTS.DEEP_LINK, this.handleDeepLink);
    this.$events.on(UI_EVENTS.ALERT, this.showErrorModal);
    this.$events.on(UI_EVENTS.START_LOADING, this.startLoading);
    this.$events.on(UI_EVENTS.STOP_LOADING, this.stopLoading);
    window.addEventListener('offline', this.showNoNetwork);
    window.addEventListener('online', this.showNetworkRestored);
    window.addEventListener('error', event => {
      console.log(`[ERROR] ${event.message}`);
    });
    AudioManager.loadAllAudio(this.$i18n.locale);
    CacheManager.loadAllGif();
  },
  async mounted() {
    // drop the scanner credentials older builds left behind on this device
    Storage.removeStoreConfigs();
    this.restoreScannersConfigs();
    await this.autoAuth();
    // deep links can navigate, and the router guard reads the token from the
    // store, so they have to wait until autoAuth has restored it
    this.handleUrlParams();
    if (!this.appReady) {
      console.log('Try initializing web BT');
      await WebBT.init();
    }
    this.$events.emit(UI_EVENTS.APP_MOUNTED);
  },
  data: () => ({
    showUI: true,
    isLoading: false,
  }),
  methods: {
    ...mapActions('app', [
      'displayGarment',
      'closeGarment',
      'toggleGarment',
      'setAppReady',
      'restoreScannersConfigs',
      'setExitOnScanComplete',
    ]),
    ...mapActions('user', ['setTempAccess']),
    ...mapActions('hardware', ['toggleArmDetection']),
    handleUrlParams() {
      console.log('handleUrlParams: ', window.location.href);
      const { redirect } = this.$route.query;
      if (redirect && redirect.startsWith('/links')) {
        this.handleDeepLink(`https://cloudzet.tg3ds.com${redirect}`);
      }
      if (typeof this.$route.query.autoexit !== 'undefined') {
        console.log('autoexit flag found');
        this.setExitOnScanComplete(true);
      }
    },
    startLoading(hideUI) {
      if (this.isLoading) return;

      this.isLoading = true;
      this.$loadingPage.open();
      this.showUI = typeof hideUI === 'boolean' ? !hideUI : true;
    },
    stopLoading() {
      if (!this.isLoading) return;

      this.$loadingPage.close();
      this.showUI = true;
      this.isLoading = false;
    },
    showErrorModal(text) {
      const width = getCurrentBreakpoint() !== 'xs' ? '60%' : '90%';
      this.$modal.show(ErrorModal, { text }, { width });
    },
    showNoNetwork() {
      console.log('[WARN] NO NETWORK');
      this.$toasted.error(this.$t('generic.network_error'), { duration: 0 });
    },
    showNetworkRestored() {
      console.log('NETWORK RESTORED');
      this.$toasted.show(this.$i18n.t('generic.network_restored'));
    },
    async loginWithPreExistingAuth(auth) {
      if (!auth) return;

      console.log('got pre-existing auth', auth.username);
      Storage.setUserAuth(auth);
      this.$events.emit(EVENTS.CLEAR_AUTH);
      await this.autoAuth();
    },
    async onUserLoginDone(account) {
      this.showUI = false;
      let hideLoading = true;
      try {
        ApiActions.mtm.getScannersConfigs().catch(() => {});
        const { path, query = {} } = this.$route;
        const { resolved } = this.$router.resolve(path);
        const pathIsValid = resolved.matched.length > 0;
        const profile = await ApiActions.user.getProfile();
        if (!profile.nick_name && account && this.isAuthenticated) {
          profile.nick_name = account; // set account as nick name by default for input convenience
        }
        console.log('profile', profile);
        console.log('path', path, pathIsValid);
        if (path && path === '/share') {
          // prevent redirect to other pages when accessing share link
          return;
        }
        const next = query.redirect || (pathIsValid && path);
        if (!profile.gender || !profile.nick_name) {
          this.$events.goto('SettingUserData', { params: { profile } });
          console.log('some user info is missing');
        } else if (next && next !== '/' && next !== '/login' && !next.startsWith('/links')) {
          hideLoading = next !== '/home';
          await this.$router.replace({ path: next });
        } else {
          hideLoading = false;
          this.$events.goto('HomePage');
        }
        if (this.garmentSource) {
          this.toggleGarment(true);
        }
      } catch (e) {
        console.log(e.message);
        if (!this.showUI) {
          this.showUI = true;
        }
      } finally {
        if (hideLoading) {
          this.$events.stopLoading();
        }
      }
    },
    async autoAuth() {
      this.$events.startLoading(true);
      const mtmUserLoggedIn = await this.autoMtmAuth();
      let userLoggedIn = false;
      const { token: userAccessToken } = this.$route.query;

      if (userAccessToken && typeof userAccessToken === 'string') {
        console.log('log user in with access token provided in url');
        await this.autoUserAuthWithAccessToken(userAccessToken);
        this.setTempAccess(true);
      } else if (!mtmUserLoggedIn) {
        userLoggedIn = await this.autoUserAuth();
      }
      if (mtmUserLoggedIn || !userLoggedIn) {
        this.$events.stopLoading();
      }
    },
    async autoUserAuthWithAccessToken(token) {
      try {
        ApiActions.user.setToken(token);
        await this.onUserLoginDone();
      } catch (e) {
        console.log(e.message);
        ApiActions.user.logout();
        this.$events.stopLoading();
      }
    },
    async autoUserAuth() {
      const auth = Storage.getUserAuth();
      if (!auth || !auth.username) return false;

      try {
        ApiActions.user.setAuth(auth);
        await ApiActions.user.auth(auth);
        await this.onUserLoginDone();
        return true;
      } catch (e) {
        console.log(e.message);
        ApiActions.user.logout();
        return false;
      }
    },
    startUserLogin() {
      this.toggleGarment(false);
      this.$events.goto('Login');
    },
    async autoMtmAuth() {
      const auth = Storage.getMtmUserAuth();
      if (!auth || !auth.account) return false;

      try {
        ApiActions.mtm.setAuth(auth);
        await ApiActions.mtm.auth(auth);
        await this.onMtmUserLoginDone();
        return true;
      } catch (e) {
        console.log(e.message);
        ApiActions.mtm.logout();
        return false;
      }
    },
    async onMtmUserLoginDone() {
      this.showUI = false;
      await ApiActions.mtm.getMe();
      await ApiActions.mtm.getRemoteConfig();
      await ApiActions.mtm.getScannersConfigs().catch(() => {});
      const audioTarget =
        this.mode === 'default' ? this.$i18n.locale : this.mode || this.$i18n.locale;
      await AudioManager.loadAllAudio(audioTarget);
      if (this.isStamp) {
        await AudioManager.waitForLoadEnd();
      }
      if (this.isContactless) {
        this.$events.emit(EVENTS.START_BT_SCAN, true);
        this.$events.goto('Welcome');
      }
      this.$events.stopLoading();
    },
    handleBTerror(error) {
      if (this.isWeb) return; // skip BT error for web

      this.leaveScanOnBTError(error);
    },
    handleDataError(error) {
      if (this.isWeb) return;

      this.leaveScanOnBTError(error, true);
    },
    leaveScanOnBTError(error, dataError) {
      if (
        this.$route.path === '/discovery' ||
        !ScanProcess.isOngoing() ||
        ScanProcess.withHandles()
      )
        return;

      // Don't leave if haven't received any Bluetooth data
      if (ScanProcess.latestUpdateArmTime === 0) return;
      // Auto reconnect
      const { scannerId } = ScanProcess;
      if (
        ScanProcess.latestUpdateArmTime > 1 &&
        Date.now() - ScanProcess.latestUpdateArmTime < 30000
      ) {
        throttleConnect(() => {
          if (ScanProcess.latestUpdateArmTime === 0) {
            console.log('[WARN] latestUpdateArmTime is 0, skip reconnect');
            return;
          }
          console.log(`[WARN] BT${dataError ? ' DATA' : ''} ERROR`, error);
          this.toggleArmDetection(true);
          this.$events.emit(EVENTS.START_ARM_DETECTION, scannerId, 1);
        });
        return;
      }

      console.log(`[ERROR] BT${dataError ? ' DATA' : ''} ERROR`, error);
      const errorMessage = this.$t('generic.bluetooth_error');
      ScanProcess.cancel().then(() => {
        // disconnect BT and reset BT state
        this.$events.emit(EVENTS.STOP_ARM_DETECTION, scannerId);
        this.toggleArmDetection(false);
        this.$events.emit(EVENTS.START_BT_SCAN, true);
        setTimeout(() => {
          this.$events.emit(EVENTS.STOP_BT_SCAN);
        }, 300);
        if (this.isContactless) {
          ApiActions.user.logout();
        }
        this.$events.alert(`${this.$t('welcome.unexpected_error')}<br><br>${errorMessage}`);
        this.$events.goto(this.isContactless ? 'Welcome' : 'HomePage');
      });
    },
  },
  computed: {
    ...mapState('app', ['mode', 'appReady', 'showMenu', 'showGarment', 'garmentSource']),
    ...mapGetters('app', ['isStamp', 'isWeb', 'isIncein']),
    ...mapGetters('mtmUser', ['isContactless', 'isAuthenticated']),
    ...mapGetters('hardware', ['hasArmDetectionStarted']),
    hideView() {
      return this.showGarment || this.isScanningQr || !this.showUI;
    },
    isKorean() {
      return this.$root.$i18n.locale === 'ko';
    },
  },
  watch: {
    isStamp(nv) {
      const className = this.isIncein ? 'incein' : 'stamp';
      if (nv) {
        document.body.classList.add(className);
      } else {
        document.body.classList.remove(className);
      }
    },
    isKorean: {
      handler(nv) {
        if (nv) {
          document.body.classList.add('ko');
        } else {
          document.body.classList.remove('ko');
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss">
@font-face {
  font-family: 'Noto Sans KR';
  font-weight: normal;
  src: url('/fonts/NotoSansCJKkr/NotoSansCJKkr-Regular.ttf') format('truetype');
}
@font-face {
  font-family: 'Noto Sans KR';
  font-weight: 500;
  src: url('/fonts/NotoSansCJKkr/NotoSansCJKkr-Medium.ttf') format('truetype');
}
@font-face {
  font-family: 'Noto Sans KR';
  font-weight: 700;
  src: url('/fonts/NotoSansCJKkr/NotoSansCJKkr-Bold.ttf') format('truetype');
}

#app {
  font-weight: 500;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow: hidden;
  height: 100%;
  width: 100%;
  display: flex;
  margin: 0 auto;
  user-select: none;
  * {
    -webkit-tap-highlight-color: transparent;
  }
}

body {
  font-family: 'Poppins', sans-serif;
  &.stamp {
    background-color: rgb(14, 14, 14);
    font-family: 'Yu Gothic', sans-serif;
  }
  &.incein {
    @import url('https://fonts.googleapis.com/css2?family=Noto+Sans&display=swap');
    background-color: #fafaf9;
    font-family: 'Noto Sans JP', Padauk, sans-serif;
    color: #262326;
  }
  &.ko {
    font-family: 'Noto Sans KR', Padauk, sans-serif;
  }
}

canvas {
  &:focus {
    outline: none;
  }
}
.cloudzet-toaster-container {
  margin-bottom: constant(safe-area-inset-bottom);
  margin-bottom: env(safe-area-inset-bottom);
}
.toasted.cloudzet-toaster.toasted-primary.default {
  padding: 20px;
  @apply text-lg;
  @screen md {
    @apply text-xl;
  }
}
</style>
